// Section for import npm components -----------------------------------------------------------------------------------
import React, { useEffect, useState, useContext } from 'react';

// MUI components
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';

// Section for import custom components --------------------------------------------------------------------------------
import CommonUtility from '@utils/Common';
import { AppConfigContext } from '@providers/AppConfigProvider';
import {
  SelectedStationsContext,
  useUpdateSelectedStations,
} from '@providers/MansionNavigation/SelectedStationsProvider';
import { useMeta } from '@providers/MansionNavigation/MetaProvider';
import { useUpdateSelectedLines } from '@providers/MansionNavigation/SelectedLinesProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import filterEntries from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';
import CustomCheckboxEvent from '@/types/event/CustomCheckboxEvent';
import LineListData, { StationListData } from '@/types/components/MansionNavigation/StationsListData';
import { StationApiData, LineApiData } from '@/types/components/MansionNavigation/StationsApiData';
import SelectedLine from '@/types/components/MansionNavigation/SelectedLine';

const commonUtil = CommonUtility;
const stationsJsonUrl: string = '/karte/api/v1/stations/';

export default function StationsList(props: any) {
  const { selectedPrefecture } = props;
  const { selectedLine } = props;
  const { isMobile } = useContext(AppConfigContext);
  const { meta } = useMeta();

  const filterQuery = filterEntries(meta.filters, ['line_id'])
    ? `&${filterEntries(meta.filters, ['line_id'])}${generateSortQuery(meta.sort, '&')}`
    : `${generateSortQuery(meta.sort, '&')}`;

  const arrSelectedLines: Number[] = selectedLine.map((line: any) => line.id);
  let apiQueryParameter: string;
  if (selectedLine.length > 1) {
    apiQueryParameter = `?prefecture_code=${selectedPrefecture.pref_code_name}&line_id=${arrSelectedLines.join(',')}`;
  } else {
    apiQueryParameter = `?prefecture_code=${selectedPrefecture.pref_code_name}&line_code=${selectedLine[0].line_name_alphabet}`;
  }

  const setSelectedLines = useUpdateSelectedLines();

  const stateLinesData: any = [];
  const [linesData, setLinesData] = useState(stateLinesData);

  const selectedStations = useContext(SelectedStationsContext);
  const setSelectedStations = useUpdateSelectedStations();

  function getPreSelectedLineIds(): number[] {
    return selectedLine.map((line: SelectedLine) => line.id);
  }

  function getPreSelectedStations(): number[] {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const preSelectedStationIds: string | null = urlParams.get('pre_selected_station_id');
    const returnValue: string[] = preSelectedStationIds ? preSelectedStationIds.split(',') : [];
    return commonUtil.convertTypeArrayNumber(returnValue);
  }

  function grantPreSelected(StationsDataId: number, checkPreSelectedStationIds: number[]): boolean {
    let returnValue: boolean = false;
    const result = checkPreSelectedStationIds.find(
      (preSelectedStationId: number) => preSelectedStationId === StationsDataId,
    );

    if (result !== undefined) {
      returnValue = true;
    }
    return returnValue;
  }

  function grantSelected(checkLinesData: Array<LineListData>, checkSelectedStationIds: number[]): void {
    checkLinesData.forEach((line) => {
      line.stations.forEach((station: StationListData) => {
        // eslint-disable-next-line no-param-reassign
        station.selected = checkSelectedStationIds.includes(Number(station.id));
      });
    });
  }

  const getStations = (url: string) => {
    return new Promise((resolve) => {
      fetch(url)
        .then((res) => res.json())
        .then((data) => resolve(data));
    });
  };

  const loadLines = async (data: any, checkPreSelectedStationIds: number[]): Promise<void> => {
    if (data.results && data.results.lines && data.results.lines.length > 0) {
      const LinesRecord: any[] = [];

      const lineProcesses = data.results.lines.map(async (line: LineApiData) => {
        // Lineの基本情報を先に定義
        const lineRecord = {
          line_id: line.line_id,
          mrweb_line_id: line.mrweb_line_id,
          line_name: line.line_name,
          line_name_alphabet: line.line_name_alphabet,
          stations: [] as any[],
        };

        // 各駅のデータを非同期で取得してlineRecordに格納
        lineRecord.stations = await Promise.all(
          line.stations.map((station: StationApiData) => {
            const stationRecord: StationListData = {
              key: `stations-${commonUtil.convertNumberToString(station.id)}`,
              line_name: station.line_name,
              line_name_alphabet: station.line_name_alphabet,
              line_id: station.line_id,
              mrweb_line_id: station.mrweb_line_id,
              mrweb_station_id: station.mrweb_station_id,
              station_id: station.id,
              id: station.id,
              id_number: station.id,
              ekidata_jp_station_id: station.ekidata_jp_station_id,
              mrweb_station_g_id: station.mrweb_station_g_id,
              station_name: station.station_name,
              station_name_kana: station.station_name_kana,
              station_name_alphabet: station.station_name_alphabet,
              pref_id: station.pref_id,
              homes_station_cd: station.homes_station_cd,
              ekidata_jp_sort_id: station.ekidata_jp_sort_id,
              primary_sort_id: station.primary_sort_id,
              secondary_sort_id: station.secondary_sort_id,
              mansion_count: station.mansion_count,
              estate_mansion_count: station.estate_mansion_count,
              selected_lines: arrSelectedLines.join(','),
              selected: grantPreSelected(station.id, checkPreSelectedStationIds),
              created_at: station.created_at,
              updated_at: station.updated_at,
            };
            return stationRecord;
          }),
        );

        return lineRecord;
      });

      LinesRecord.push(...(await Promise.all(lineProcesses)));

      // console.log(LinesRecord);
      setLinesData(LinesRecord);
    }
  };

  useEffect(() => {
    const preSelectedLineIds: number[] = getPreSelectedLineIds();
    setSelectedLines(preSelectedLineIds);

    const preSelectedStationIds: number[] = getPreSelectedStations();
    setSelectedStations(preSelectedStationIds);

    const fetchStationsData = async (): Promise<void> => {
      const data: any = await getStations(stationsJsonUrl + apiQueryParameter);
      loadLines(data, preSelectedStationIds).then();
    };
    fetchStationsData().then();
  }, []);

  const handleChange = (event: CustomCheckboxEvent): void => {
    let formSelectedStationsArray: number[] = selectedStations.concat();
    const stationId = Number(event.target.id.replace('checkbox-', ''));

    if (event.target.checked) {
      formSelectedStationsArray.push(stationId);
    } else {
      formSelectedStationsArray = formSelectedStationsArray.filter((id) => id !== stationId);
    }

    setSelectedStations(formSelectedStationsArray);
    grantSelected(linesData, formSelectedStationsArray);
  };

  // Section for render component --------------------------------------------------------------------------------------
  return (
    <Box className="md:mx-24">
      {isMobile ? (
        // Section for render Mobile mode ------------------------------------------------------------------------------
        <Container className="p-0">
          {linesData.map((line: LineListData) => (
            <React.Fragment key={`line-${line.line_id}`}>
              <List className="stations_list mb-8 last:mb-1 p-0  border-b-[#D9D9D9] border-b border-solid">
                <Box className="pb-3 border-b-[#D5D5D5] border-b border-solid">
                  <Typography
                    variant="h2"
                    gutterBottom
                    className="text-lg font-bold tracking-normal mb-0 mx-5 pl-[18px] border-l-2 border-l-[#191919] border-solid"
                  >
                    {line.line_name}
                  </Typography>
                </Box>
                {line.stations.map((station: StationListData) => (
                  <ListItem
                    key={`station-${station.id}-item`}
                    className="stations relative w-[calc(100%_-_20px)] ml-5 border-b-[#D9D9D9] border-b border-solid p-0"
                  >
                    {station.mansion_count > 0 ? (
                      <>
                        <Checkbox
                          className="stations_check mr-3 p-0"
                          onChange={handleChange}
                          id={`checkbox-${station.id}`}
                          checked={station.selected}
                        />
                        <CheckIcon className="stations_check_icon hidden absolute w-5 h-5 text-white" />
                        <Link
                          href={`/karte/${selectedPrefecture.pref_code_name}/stations/${station.station_name_alphabet}/?line_id=${station.selected_lines}&station_id=${station.id}${filterQuery}`}
                          className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] no-underline"
                        >
                          {station.station_name}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Checkbox className="stations_check opacity-80" id={`checkbox-${station.id}`} disabled />
                        <CheckIcon className="stations_check_icon hidden absolute w-5 h-5 text-white" />
                        <Box className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] opacity-50 ">
                          {station.station_name}
                        </Box>
                      </>
                    )}
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          ))}
        </Container>
      ) : (
        // Section for render PC mode ----------------------------------------------------------------------------------
        <Box>
          {linesData.map((line: LineListData) => (
            <React.Fragment key={`line-${line.line_id}`}>
              <Box className="w-full mb-6 pb-3 border-b-[#D5D5D5] border-b border-solid">
                <Typography
                  variant="h2"
                  gutterBottom
                  className="text-lg font-bold leading-6 tracking-[0.08em] text-left pl-4 border-l-2 border-[#191919] mb-0"
                >
                  {line.line_name}
                </Typography>
              </Box>
              <List className="flex flex-wrap gap-y-6 justify-start mb-12 last:mb-16 p-0">
                {line.stations.map((station: StationListData) => (
                  <ListItem key={`station-${station.id}-item`} className="stations relative flex p-0 w-1/4 self-center">
                    {station.mansion_count > 0 ? (
                      <>
                        <Checkbox
                          className="stations_check"
                          onChange={handleChange}
                          id={`checkbox-${station.id}`}
                          checked={station.selected}
                        />
                        <CheckIcon className="stations_check_icon hidden absolute w-5 h-5 text-white" />
                        <Link
                          href={`/karte/${selectedPrefecture.pref_code_name}/stations/${station.station_name_alphabet}/?line_id=${station.selected_lines}&station_id=${station.id}${filterQuery}`}
                          className="text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] rf-hover-text"
                        >
                          {station.station_name}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Checkbox className="stations_check opacity-80" id={`checkbox-${station.id}`} disabled />
                        <CheckIcon className="stations_check_icon hidden absolute w-5 h-5 text-white" />
                        <Box className="block w-full text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] opacity-50 ">
                          {station.station_name}
                        </Box>
                      </>
                    )}
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
}
